import React, { useEffect, useState } from "react";
import { BorderedContainer } from "../components/BorderedContainer";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { Footer } from "../components/Footer";
import { Header } from "../components/header/Header";
import { useTranslation } from "react-i18next";
import { UserRegistrationForm } from "../components/auth/UserRegistrationForm";
import { EnterFriendsEmailForm } from "components/promo/EnterFriendsEmailForm";
import { useAppSelector } from "store/hooks";
import inGamePng from "../assets/images/landingPage/In-game.webp";
import { IsRunningOverwolf } from "utilityFunctions";
import useApplicationInfo from "hooks/useAppInfo";
import { InGameAppDownloadButton } from "components/promo/InGameAppDownloadButton";
import { SafeAreaWrapper } from "components/SafeAreaWrapper";
import BackgroundImage from "../assets/images/landingPage/Secondary_Background.webp";
import Aos from "aos";
import { useWindowSize } from "hooks/useWindowSize";

export interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface Props {
  onlySocial?: boolean;
}

export const SignUpPage: React.FC<Props> = ({ onlySocial }) => {
  const [emailInUseError, setEmailInUseError] = useState<boolean>(false);
  const [forbiddenError, setForbiddenError] = useState<boolean>(false);
  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [discordCode, setDiscordCode] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const [width, height] = useWindowSize();
  const { application } = useApplicationInfo();
  const isRunningOverwolf = IsRunningOverwolf();
  const isRunningIos = application === "IOS_CLIENT";
  const isRunningCapacitor = application === "ANDROID_CLIENT" || isRunningIos;
  const params = useParams<{
    qrTokenToValidate?: string;
    promoCode?: string;
  }>();
  const { pathname } = useLocation();
  const onSocialSignupPage = pathname.slice(0, 13) === "/socialSignup";

  const [showForm, setShowForm] = useState<boolean>(
    isRunningIos ? true : false
  );
  const isTouchDevice =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isWebAppOnIOS =
    isTouchDevice && /iPad|iPhone|iPod/.test(navigator.userAgent);

  const overwolfOrCapacitorIsRunning = isRunningOverwolf || isRunningCapacitor;

  const REACT_APP_WEBAPP_URL = useAppSelector(
    (state) => state?.auth?.env?.REACT_APP_WEBAPP_URL
  );

  const BackgroundImageUrl = `${REACT_APP_WEBAPP_URL}/images/Secondary_Background_New.jpg`;
  const homeBackgroundToUse = IsRunningOverwolf()
    ? BackgroundImageUrl
    : BackgroundImage;

  const { t } = useTranslation();
  const stopLoginWithToken = React.useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!!params.promoCode) {
      localStorage.setItem("promoCode", params.promoCode);
    }
  }, [params.promoCode]);

  useEffect(() => {
    Aos.init({ duration: 500 });
  }, []);

  if (
    !!accessToken &&
    !params.qrTokenToValidate &&
    !googleToken &&
    !discordCode &&
    !userData
  ) {
    return <Navigate to="/dashboard/mymeals" />;
  }

  return (
    <SafeAreaWrapper>
      <div className="pb-96 h-screen">
        <div
          className={`${
            isWebAppOnIOS ? "background-IOS" : ""
          } gradientBackground absolute top-0 left-0 w-full h-full bg-cover -z-30 opacity-100`}
        />
        <div
          className="absolute top-0 left-0 w-full h-full bg-cover -z-20 opacity-50"
          style={{
            backgroundImage: `url(${homeBackgroundToUse})`,
          }}
        />
        <Header />
        {!!showForm || isRunningOverwolf || width < 1024 ? (
          <>
            <div
              className="mt-40 lg:mt-44 2xl:mt-48 pb-20 flex flex-col mx-auto w-full h-full justify-center items-center"
              data-aos="fade-in"
            >
              {(!!googleToken || !!discordCode || !!userData) && (
                <EnterFriendsEmailForm
                  discordCode={discordCode}
                  googleToken={googleToken}
                  setEmailInUseError={setEmailInUseError}
                  setForbiddenError={setForbiddenError}
                  qrTokenToValidate={params.qrTokenToValidate}
                  userData={userData}
                  setUserData={setUserData}
                  emailInUseError={emailInUseError}
                  forbiddenError={forbiddenError}
                />
              )}

              {!googleToken && !discordCode && !userData && (
                <>
                  <BorderedContainer
                    height={`${onlySocial ? "250px" : undefined}`}
                    className="w-full md:w-3/5 lg:w-3/5 xl:w-1/2 2xl:w-5/12 3xl:w-1/3 max-w-4xl mx-auto mt-6"
                  >
                    <div className="w-10/12 md:w-full mx-auto text-slate-300">
                      <h1 className="text-xl lg:text-2xl xl:text-3xl text-center font-semibold pb-4 sm:pb-6">
                        {t("form.createAnAccount")}
                      </h1>
                      <div>
                        <UserRegistrationForm
                          setDiscordCode={setDiscordCode}
                          setGoogleToken={setGoogleToken}
                          setForbiddenError={setForbiddenError}
                          setEmailInUseError={setEmailInUseError}
                          forbiddenError={forbiddenError}
                          setUserData={setUserData}
                          qrTokenToValidate={params.qrTokenToValidate}
                          onlySocial={onlySocial}
                          stopLoginWithToken={stopLoginWithToken}
                          setShowForm={setShowForm}
                          showForm={true}
                        />
                      </div>
                      <div className="hidden md:block text-center lg:px-5">
                        <div className="sm:flex sm:justify-center text-slate-400">
                          {t("form.alreadyAccount")}
                          <Link
                            to={`/login${
                              params.qrTokenToValidate
                                ? `/${params.qrTokenToValidate}`
                                : ""
                            }`}
                            draggable={false}
                          >
                            <div className="hover:underline text-green-500 pl-1">
                              {t("form.signInHere")}
                            </div>
                          </Link>
                        </div>
                      </div>
                      {emailInUseError && (
                        <div className="w-full flex justify-center items-center">
                          <small className="text-red-500 text-center">
                            {t("error.emailInUse")}
                          </small>
                        </div>
                      )}
                    </div>
                  </BorderedContainer>
                </>
              )}
            </div>
          </>
        ) : (
          <div
            className={`${
              onSocialSignupPage
                ? "mt-28 lg:mt-32"
                : !isRunningIos
                ? "mt-32 lg:mt-44"
                : "mt-6"
            } pb-20 flex flex-col mx-auto w-full h-full justify-center items-center`}
            data-aos="fade-in"
          >
            <h1
              className={`${
                height > 800 ? "flex" : "hidden"
              } text-2xl md:text-3xl xl:text-4xl text-slate-200 text-center font-semibold w-full justify-center`}
            >
              {t("landingPage.welcomeToTeamLiifer")}
            </h1>
            <div className="flex w-full pt-10 justify-center items-center">
              {(!!googleToken || !!discordCode || !!userData) && (
                <EnterFriendsEmailForm
                  discordCode={discordCode}
                  googleToken={googleToken}
                  setEmailInUseError={setEmailInUseError}
                  setForbiddenError={setForbiddenError}
                  qrTokenToValidate={params.qrTokenToValidate}
                  userData={userData}
                  setUserData={setUserData}
                  emailInUseError={emailInUseError}
                  forbiddenError={forbiddenError}
                />
              )}

              {!googleToken && !discordCode && !userData && (
                <>
                  {!isRunningCapacitor && !isRunningOverwolf && !onlySocial && (
                    <div className="hidden lg:flex flex-col justify-center items-center text-center space-y-6 md:ml-20 xl:-ml-10">
                      <img
                        className="h-56 xl:h-80"
                        src={inGamePng}
                        alt="ingame app"
                      />
                      <InGameAppDownloadButton centered boldText />
                    </div>
                  )}
                  {!isRunningCapacitor && !isRunningOverwolf && !onlySocial && (
                    <div className="hidden lg:block xl:pl-4 lg:pr-8 xl:pr-16 -mt-32">
                      <span className="text-2xl text-slate-200  font-semibold">
                        {t("landingPage.or").toUpperCase()}
                      </span>
                    </div>
                  )}
                  <BorderedContainer className="w-full mx-1 sm:mx-16 md:mx-36 lg:mx-0 md:w-2/3 lg:w-104 xl:w-124 mb-4">
                    <div className="w-10/12 md:w-full mx-auto text-slate-300">
                      <h1 className="text-xl text-center font-semibold pt-2 pb-4">
                        {t("form.signUp")}
                      </h1>
                      <div>
                        <UserRegistrationForm
                          setDiscordCode={setDiscordCode}
                          setGoogleToken={setGoogleToken}
                          setForbiddenError={setForbiddenError}
                          setEmailInUseError={setEmailInUseError}
                          forbiddenError={forbiddenError}
                          setUserData={setUserData}
                          qrTokenToValidate={params.qrTokenToValidate}
                          onlySocial={onlySocial}
                          stopLoginWithToken={stopLoginWithToken}
                          setShowForm={setShowForm}
                          showForm={showForm}
                        />
                      </div>
                      <div className="text-center lg:px-5 mt-3">
                        <div className="text-slate-400">
                          {t("form.alreadyAccount")}
                          <Link
                            to={`/login${
                              params.qrTokenToValidate
                                ? `/${params.qrTokenToValidate}`
                                : ""
                            }`}
                            draggable={false}
                          >
                            <div className="hover:underline text-green-500 pl-1">
                              {t("form.signInHere")}
                            </div>
                          </Link>
                        </div>
                      </div>
                      {emailInUseError && (
                        <div className="w-full flex justify-center items-center">
                          <small className="text-red-500 text-center">
                            {t("error.emailInUse")}
                          </small>
                        </div>
                      )}
                    </div>
                  </BorderedContainer>
                </>
              )}
            </div>
          </div>
        )}

        {!overwolfOrCapacitorIsRunning && (
          <div className="block md:hidden pt-64">
            <Footer />
          </div>
        )}
      </div>
    </SafeAreaWrapper>
  );
};

export default SignUpPage;
